import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'

function ChartOfAccounts({ companyId }) {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAccountModal, setShowAccountModal] = useState(false)
  const [editingAccount, setEditingAccount] = useState(null)
  const [seeding, setSeeding] = useState(false)
  const [accountForm, setAccountForm] = useState({
    account_code: '',
    account_name: '',
    account_type: 'asset',
    category: 'Current Assets',
    normal_balance: 'debit',
    description: '',
    is_active: true,
    initial_balance: ''  // ← Empty string instead of 0
  })

  // Default Chart of Accounts for new companies
  const defaultAccounts = [
    // ===== ASSETS (1000-1999) =====
    { account_code: '1000', account_name: 'Cash', account_type: 'asset', category: 'Current Assets', normal_balance: 'debit', description: 'Cash on hand and in bank accounts' },
    { account_code: '1010', account_name: 'Bank Account - Operating', account_type: 'asset', category: 'Current Assets', normal_balance: 'debit', description: 'Primary operating bank account' },
    { account_code: '1020', account_name: 'Bank Account - Savings', account_type: 'asset', category: 'Current Assets', normal_balance: 'debit', description: 'Savings bank account' },
    { account_code: '1100', account_name: 'Accounts Receivable', account_type: 'asset', category: 'Current Assets', normal_balance: 'debit', description: 'Money owed by clients' },
    { account_code: '1150', account_name: 'Properties for Sale', account_type: 'asset', category: 'Inventory', normal_balance: 'debit', description: 'Properties available for sale' },
    { account_code: '1200', account_name: 'Office Equipment', account_type: 'asset', category: 'Fixed Assets', normal_balance: 'debit', description: 'Computers, desks, and office equipment' },
    { account_code: '1210', account_name: 'Vehicles', account_type: 'asset', category: 'Fixed Assets', normal_balance: 'debit', description: 'Company vehicles' },
    { account_code: '1250', account_name: 'Accumulated Depreciation', account_type: 'asset', category: 'Fixed Assets', normal_balance: 'credit', description: 'Accumulated depreciation on fixed assets' },
    { account_code: '1300', account_name: 'Prepaid Expenses', account_type: 'asset', category: 'Current Assets', normal_balance: 'debit', description: 'Prepaid insurance, rent, etc.' },
    
    // ===== LIABILITIES (2000-2999) =====
    { account_code: '2000', account_name: 'Accounts Payable', account_type: 'liability', category: 'Current Liabilities', normal_balance: 'credit', description: 'Money owed to vendors' },
    { account_code: '2010', account_name: 'Salaries Payable', account_type: 'liability', category: 'Current Liabilities', normal_balance: 'credit', description: 'Unpaid salaries' },
    { account_code: '2020', account_name: 'Tax Payable', account_type: 'liability', category: 'Current Liabilities', normal_balance: 'credit', description: 'Taxes owed to government' },
    { account_code: '2100', account_name: 'Client Deposits', account_type: 'liability', category: 'Current Liabilities', normal_balance: 'credit', description: 'Client deposits held in trust' },
    { account_code: '2200', account_name: 'Bank Loan', account_type: 'liability', category: 'Long-term Liabilities', normal_balance: 'credit', description: 'Long-term bank loans' },
    
    // ===== EQUITY (3000-3999) =====
    { account_code: '3000', account_name: "Owner's Equity", account_type: 'equity', category: 'Equity', normal_balance: 'credit', description: "Owner's capital investment" },
    { account_code: '3010', account_name: 'Retained Earnings', account_type: 'equity', category: 'Equity', normal_balance: 'credit', description: 'Cumulative earnings retained in business' },
    { account_code: '3100', account_name: 'Current Year Earnings', account_type: 'equity', category: 'Equity', normal_balance: 'credit', description: 'Earnings for the current fiscal year' },
    
    // ===== REVENUE (4000-4999) =====
    { account_code: '4000', account_name: 'Property Sales Revenue', account_type: 'revenue', category: 'Operating Revenue', normal_balance: 'credit', description: 'Revenue from property sales' },
    { account_code: '4010', account_name: 'Commission Income', account_type: 'revenue', category: 'Operating Revenue', normal_balance: 'credit', description: 'Commission earned on sales' },
    { account_code: '4020', account_name: 'Rental Income', account_type: 'revenue', category: 'Operating Revenue', normal_balance: 'credit', description: 'Revenue from rental properties' },
    { account_code: '4030', account_name: 'Consulting Fees', account_type: 'revenue', category: 'Operating Revenue', normal_balance: 'credit', description: 'Consulting service fees' },
    { account_code: '4100', account_name: 'Interest Income', account_type: 'revenue', category: 'Other Income', normal_balance: 'credit', description: 'Interest earned on investments' },
    
    // ===== EXPENSES (5000-5999) =====
    { account_code: '5000', account_name: 'Salaries Expense', account_type: 'expense', category: 'Operating Expenses', normal_balance: 'debit', description: 'Staff salary costs' },
    { account_code: '5010', account_name: 'Marketing Expense', account_type: 'expense', category: 'Operating Expenses', normal_balance: 'debit', description: 'Advertising and marketing costs' },
    { account_code: '5020', account_name: 'Office Rent', account_type: 'expense', category: 'Operating Expenses', normal_balance: 'debit', description: 'Office rental costs' },
    { account_code: '5030', account_name: 'Utilities', account_type: 'expense', category: 'Operating Expenses', normal_balance: 'debit', description: 'Electricity, water, internet' },
    { account_code: '5040', account_name: 'Legal Fees', account_type: 'expense', category: 'Operating Expenses', normal_balance: 'debit', description: 'Legal service fees' },
    { account_code: '5050', account_name: 'Insurance', account_type: 'expense', category: 'Operating Expenses', normal_balance: 'debit', description: 'Business insurance premiums' },
    { account_code: '5060', account_name: 'Maintenance', account_type: 'expense', category: 'Operating Expenses', normal_balance: 'debit', description: 'Property and equipment maintenance' },
    { account_code: '5070', account_name: 'Travel & Entertainment', account_type: 'expense', category: 'Operating Expenses', normal_balance: 'debit', description: 'Business travel and client entertainment' },
    { account_code: '5080', account_name: 'Software Subscriptions', account_type: 'expense', category: 'Operating Expenses', normal_balance: 'debit', description: 'CRM, software, and tool subscriptions' },
    { account_code: '5090', account_name: 'Depreciation', account_type: 'expense', category: 'Operating Expenses', normal_balance: 'debit', description: 'Depreciation on fixed assets' },
    { account_code: '5100', account_name: 'Bank Charges', account_type: 'expense', category: 'Operating Expenses', normal_balance: 'debit', description: 'Bank fees and transaction costs' },
    { account_code: '5900', account_name: 'Tax Expense', account_type: 'expense', category: 'Other Expenses', normal_balance: 'debit', description: 'Corporate and other tax expenses' }
  ]

  // Seed default accounts when company first opens Chart of Accounts
  const seedDefaultAccounts = async () => {
    if (seeding) return
    setSeeding(true)
    setError('')

    try {
      console.log('Checking if accounts exist for company:', companyId)
      
      const { data: existingAccounts, error: checkError } = await supabase
        .from('chart_of_accounts')
        .select('id')
        .eq('company_id', companyId)
        .limit(1)

      if (checkError) throw checkError

      if (!existingAccounts || existingAccounts.length === 0) {
        console.log('No accounts found. Seeding default accounts...')
        
        const accountsToInsert = defaultAccounts.map(account => ({
          ...account,
          company_id: companyId,
          is_active: true,
          balance: 0,
          created_at: new Date(),
          updated_at: new Date()
        }))

        const { error: insertError } = await supabase
          .from('chart_of_accounts')
          .insert(accountsToInsert)

        if (insertError) throw insertError
        
        console.log('Default accounts seeded successfully!', accountsToInsert.length)
        return true
      } else {
        console.log('Accounts already exist. Skipping seed.')
        return false
      }
    } catch (err) {
      console.error('Error seeding default accounts:', err)
      setError('Failed to set up default accounts: ' + err.message)
      return false
    } finally {
      setSeeding(false)
    }
  }

  const fetchAccounts = async () => {
    if (!companyId) return
    setLoading(true)
    setError('')

    try {
      const { data: existingCheck, error: checkError } = await supabase
        .from('chart_of_accounts')
        .select('id')
        .eq('company_id', companyId)
        .limit(1)

      if (checkError) throw checkError

      if (!existingCheck || existingCheck.length === 0) {
        console.log('No accounts found, seeding...')
        const seeded = await seedDefaultAccounts()
        if (seeded) {
          const { data, error } = await supabase
            .from('chart_of_accounts')
            .select('*')
            .eq('company_id', companyId)
            .order('account_code', { ascending: true })

          if (error) throw error
          setAccounts(data || [])
          setLoading(false)
          return
        }
      }

      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .eq('company_id', companyId)
        .order('account_code', { ascending: true })

      if (error) throw error
      setAccounts(data || [])
    } catch (err) {
      console.error('Error fetching accounts:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (companyId) {
      fetchAccounts()
    }
  }, [companyId])

  const handleAccountSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      if (!accountForm.account_code) {
        setError('Account Code is required')
        setLoading(false)
        return
      }

      if (!accountForm.account_name) {
        setError('Account Name is required')
        setLoading(false)
        return
      }

      const { data: existingCode, error: codeCheckError } = await supabase
        .from('chart_of_accounts')
        .select('id')
        .eq('account_code', accountForm.account_code)
        .eq('company_id', companyId)
        .maybeSingle()

      if (codeCheckError) throw codeCheckError

      if (existingCode && !editingAccount) {
        setError(`Account code ${accountForm.account_code} already exists. Please use a unique code.`)
        setLoading(false)
        return
      }

      // Convert empty string to 0 for balance
      const initialBalance = accountForm.initial_balance === '' ? 0 : parseFloat(accountForm.initial_balance) || 0

      const formData = {
        account_code: accountForm.account_code,
        account_name: accountForm.account_name,
        account_type: accountForm.account_type.toLowerCase(),
        category: accountForm.category,
        normal_balance: accountForm.normal_balance.toLowerCase(),
        description: accountForm.description || '',
        is_active: accountForm.is_active !== false,
        balance: initialBalance,
        company_id: companyId,
        updated_at: new Date()
      }

      if (editingAccount) {
        const { error } = await supabase
          .from('chart_of_accounts')
          .update(formData)
          .eq('id', editingAccount.id)

        if (error) throw error
        alert('Account updated successfully!')
      } else {
        const { error } = await supabase
          .from('chart_of_accounts')
          .insert(formData)

        if (error) throw error
        alert('Account added successfully!')
      }

      setShowAccountModal(false)
      setEditingAccount(null)
      setAccountForm({
        account_code: '',
        account_name: '',
        account_type: 'asset',
        category: 'Current Assets',
        normal_balance: 'debit',
        description: '',
        is_active: true,
        initial_balance: ''  // ← Reset to empty string
      })
      fetchAccounts()
    } catch (err) {
      console.error('Error saving account:', err)
      setError(err.message)
      alert('Failed to save account: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (account) => {
    setEditingAccount(account)
    setAccountForm({
      account_code: account.account_code || '',
      account_name: account.account_name || '',
      account_type: account.account_type || 'asset',
      category: account.category || 'Current Assets',
      normal_balance: account.normal_balance || 'debit',
      description: account.description || '',
      is_active: account.is_active !== false,
      initial_balance: account.balance || ''  // ← Show balance or empty
    })
    setShowAccountModal(true)
  }

  const handleDelete = async (account) => {
    if (!confirm(`Delete account "${account.account_name}"?`)) return

    try {
      const { error } = await supabase
        .from('chart_of_accounts')
        .delete()
        .eq('id', account.id)

      if (error) throw error
      alert('Account deleted!')
      fetchAccounts()
    } catch (err) {
      console.error('Error deleting account:', err)
      alert('Failed to delete account: ' + err.message)
    }
  }

  // Group accounts by type
  const groupedAccounts = accounts.reduce((groups, account) => {
    const type = account.account_type || 'other'
    if (!groups[type]) groups[type] = []
    groups[type].push(account)
    return groups
  }, {})

  const accountTypes = [
    { value: 'asset', label: '💵 Asset', codeRange: '1000-1999' },
    { value: 'liability', label: '📋 Liability', codeRange: '2000-2999' },
    { value: 'equity', label: '🏦 Equity', codeRange: '3000-3999' },
    { value: 'revenue', label: '📈 Revenue', codeRange: '4000-4999' },
    { value: 'expense', label: '📉 Expense', codeRange: '5000-5999' }
  ]

  const categories = {
    'asset': ['Current Assets', 'Fixed Assets', 'Inventory', 'Other Assets'],
    'liability': ['Current Liabilities', 'Long-term Liabilities', 'Other Liabilities'],
    'equity': ['Equity', 'Retained Earnings'],
    'revenue': ['Operating Revenue', 'Other Income'],
    'expense': ['Operating Expenses', 'Other Expenses']
  }

  const typeEmojis = {
    'asset': '💵',
    'liability': '📋',
    'equity': '🏦',
    'revenue': '📈',
    'expense': '📉'
  }

  const typeColors = {
    'asset': '#059669',
    'liability': '#f59e0b',
    'equity': '#1e3a5f',
    'revenue': '#2c7da0',
    'expense': '#dc2626'
  }

  const typeDisplayNames = {
    'asset': 'Asset',
    'liability': 'Liability',
    'equity': 'Equity',
    'revenue': 'Revenue',
    'expense': 'Expense'
  }

  // Styles
  const containerStyle = {
    padding: '20px'
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  }

  const titleStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e3a5f',
    margin: 0
  }

  const addBtn = {
    background: '#1e3a5f',
    color: '#fff',
    border: 'none',
    padding: '10px 24px',
    borderRadius: '25px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  }

  const legendCard = {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '24px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
  }

  const legendGrid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px'
  }

  const legendItem = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '8px',
    background: '#f8fafc',
    fontSize: '13px'
  }

  const legendBadge = {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#fff'
  }

  const groupCard = {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '24px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
  }

  const groupTitle = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e3a5f',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '2px solid #e2e8f0'
  }

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  }

  const thStyle = {
    textAlign: 'left',
    padding: '12px',
    background: '#f8fafc',
    color: '#1e293b',
    fontWeight: '600',
    borderBottom: '2px solid #e2e8f0'
  }

  const tdStyle = {
    padding: '12px',
    borderBottom: '1px solid #e2e8f0'
  }

  const editBtn = {
    background: '#2c7da0',
    color: '#fff',
    border: 'none',
    padding: '4px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    marginRight: '8px',
    fontSize: '12px'
  }

  const deleteBtn = {
    background: '#dc2626',
    color: '#fff',
    border: 'none',
    padding: '4px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px'
  }

  const statusBadge = {
    active: {
      background: '#d1fae5',
      color: '#059669',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px'
    },
    inactive: {
      background: '#fee2e2',
      color: '#dc2626',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px'
    }
  }

  const modalOverlay = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  }

  const modalStyle = {
    background: '#fff',
    borderRadius: '20px',
    padding: '30px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto'
  }

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    marginBottom: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box'
  }

  const labelStyle = {
    display: 'block',
    marginBottom: '4px',
    fontWeight: '500',
    fontSize: '14px',
    color: '#1e293b'
  }

  const modalBtnStyle = {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  }

  // Loading state
  if (loading && accounts.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading Chart of Accounts...</p>
        {seeding && <p style={{ color: '#64748b', fontSize: '14px' }}>Setting up default accounts...</p>}
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h2 style={titleStyle}>📚 Chart of Accounts</h2>
        <button onClick={() => setShowAccountModal(true)} style={addBtn}>
          ➕ Add New Account
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
          ⚠️ {error}
        </div>
      )}

      {/* Legend / Account Type Guide */}
      <div style={legendCard}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e3a5f', margin: '0 0 12px 0' }}>
          📖 Account Types & Code Ranges
        </h3>
        <div style={legendGrid}>
          {accountTypes.map(({ value, label, codeRange }) => (
            <div key={value} style={legendItem}>
              <span style={{ fontSize: '18px' }}>{typeEmojis[value]}</span>
              <span style={{ flex: 1 }}>{label}</span>
              <span style={{ ...legendBadge, background: typeColors[value] }}>
                {codeRange}
              </span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '12px', fontSize: '12px', color: '#64748b', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
          💡 <strong>Note:</strong> Account balances are automatically updated when transactions are added. The <strong>Balance</strong> column shows the current total for each account. You can set an <strong>Initial Balance</strong> when creating a new account.
        </div>
      </div>

      {/* Account Groups */}
      {accountTypes.map(({ value: type }) => {
        const typeAccounts = groupedAccounts[type] || []
        if (typeAccounts.length === 0) return null

        return (
          <div key={type} style={groupCard}>
            <h3 style={{ ...groupTitle, color: typeColors[type] }}>
              {typeEmojis[type]} {typeDisplayNames[type]}s ({typeAccounts.length})
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '400', marginLeft: '12px' }}>
                Code Range: {accountTypes.find(t => t.value === type)?.codeRange}
              </span>
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Code</th>
                    <th style={thStyle}>Account Name</th>
                    <th style={thStyle}>Category</th>
                    <th style={thStyle}>Normal Balance</th>
                    <th style={thStyle}>Balance</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {typeAccounts.map(account => (
                    <tr key={account.id}>
                      <td style={tdStyle}>{account.account_code || '-'}</td>
                      <td style={tdStyle}>{account.account_name}</td>
                      <td style={tdStyle}>{account.category || '-'}</td>
                      <td style={tdStyle}>
                        <span style={{
                          color: account.normal_balance === 'debit' ? '#059669' : '#dc2626',
                          fontWeight: '500'
                        }}>
                          {account.normal_balance || '-'}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: '600', color: '#1e3a5f' }}>
                          ${(account.balance || 0).toLocaleString()}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={account.is_active !== false ? statusBadge.active : statusBadge.inactive}>
                          {account.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <button onClick={() => handleEdit(account)} style={editBtn}>Edit</button>
                        <button onClick={() => handleDelete(account)} style={deleteBtn}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      })}

      {accounts.length === 0 && !loading && !seeding && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          <p style={{ fontSize: '18px' }}>📚 No accounts found</p>
          <p>Click "Add New Account" to create your first account.</p>
        </div>
      )}

      {/* Add/Edit Account Modal */}
      {showAccountModal && (
        <div style={modalOverlay} onClick={() => {
          setShowAccountModal(false)
          setEditingAccount(null)
          setAccountForm({
            account_code: '',
            account_name: '',
            account_type: 'asset',
            category: 'Current Assets',
            normal_balance: 'debit',
            description: '',
            is_active: true,
            initial_balance: ''
          })
        }}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: '22px', color: '#1e3a5f', margin: '0 0 20px 0' }}>
              {editingAccount ? '✏️ Edit Account' : '➕ Add New Account'}
            </h2>

            {error && (
              <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                ⚠️ {error}
              </div>
            )}

            <form onSubmit={handleAccountSubmit}>
              <label style={labelStyle}>Account Code *</label>
              <input
                type="text"
                placeholder="e.g., 1050"
                value={accountForm.account_code}
                onChange={(e) => setAccountForm({ ...accountForm, account_code: e.target.value })}
                style={inputStyle}
                required
              />

              <label style={labelStyle}>Account Name *</label>
              <input
                type="text"
                placeholder="e.g., Office Equipment"
                value={accountForm.account_name}
                onChange={(e) => setAccountForm({ ...accountForm, account_name: e.target.value })}
                style={inputStyle}
                required
              />

              <label style={labelStyle}>Account Type *</label>
              <select
                value={accountForm.account_type}
                onChange={(e) => {
                  const newType = e.target.value
                  setAccountForm({
                    ...accountForm,
                    account_type: newType,
                    category: categories[newType]?.[0] || ''
                  })
                }}
                style={inputStyle}
                required
              >
                {accountTypes.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>

              <label style={labelStyle}>Category *</label>
              <select
                value={accountForm.category}
                onChange={(e) => setAccountForm({ ...accountForm, category: e.target.value })}
                style={inputStyle}
                required
              >
                {(categories[accountForm.account_type] || ['']).map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>

              <label style={labelStyle}>Normal Balance *</label>
              <select
                value={accountForm.normal_balance}
                onChange={(e) => setAccountForm({ ...accountForm, normal_balance: e.target.value })}
                style={inputStyle}
                required
              >
                <option value="debit">Debit</option>
                <option value="credit">Credit</option>
              </select>

              {/* FIXED: Initial Balance Field */}
              <label style={labelStyle}>Initial Balance</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: '#64748b' }}>$</span>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={accountForm.initial_balance === 0 ? '' : accountForm.initial_balance}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : parseFloat(e.target.value)
                    setAccountForm({ ...accountForm, initial_balance: value })
                  }}
                  style={inputStyle}
                />
              </div>
              <p style={{ fontSize: '11px', color: '#64748b', marginTop: '-8px', marginBottom: '12px' }}>
                💡 Set an initial balance for this account (e.g., starting cash balance). Leave blank for 0.
              </p>

              <label style={labelStyle}>Description</label>
              <textarea
                placeholder="Brief description of this account"
                value={accountForm.description}
                onChange={(e) => setAccountForm({ ...accountForm, description: e.target.value })}
                style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
              />

              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={accountForm.is_active}
                  onChange={(e) => setAccountForm({ ...accountForm, is_active: e.target.checked })}
                />
                Active
              </label>

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="submit" style={{ ...modalBtnStyle, background: '#059669', color: '#fff' }}>
                  {editingAccount ? 'Update Account' : 'Add Account'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAccountModal(false)
                    setEditingAccount(null)
                    setAccountForm({
                      account_code: '',
                      account_name: '',
                      account_type: 'asset',
                      category: 'Current Assets',
                      normal_balance: 'debit',
                      description: '',
                      is_active: true,
                      initial_balance: ''
                    })
                  }}
                  style={{ ...modalBtnStyle, background: '#6b7280', color: '#fff' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChartOfAccounts